import { CSSProperties } from 'react';

import { clsx } from '@freelancelabs/utils';

/**
 * `done` (passed · `current`) where the file stands · `upcoming`: not reached yet
 * · `rejected` (the file stopped here · `unreachable`) will not be reached, because
 * an earlier step rejected it.
 */
export type StepState = 'done' | 'current' | 'upcoming' | 'rejected' | 'unreachable';

export interface Step {
    name: string;
    state: StepState;
    /** Only shown for the current step; always announced to screen readers. */
    stateLabel?: string;
}

interface Props {
    steps: Step[];
    /** Describes the sequence for screen readers, e.g. « Avancement du recrutement ». */
    caption: string;
    /**
     * Attention, les deux maquettes ne s'accordent pas, et aucune n'a tort : la frise d'un
     * besoin court à l'HORIZONTALE en tête de fiche, le parcours du back-office
     * tient dans une colonne de 264 px à gauche du formulaire. Le défaut est donc
     * `vertical`, celui du back-office, parce que c'est le premier consommateur et
     * qu'un défaut silencieux doit être celui qui existait avant.
     */
    orientation?: 'horizontal' | 'vertical';
    className?: string;
}

const stepClassName: { [key in StepState]?: string } = {
    done: 'fl-stepper__step--done',
    current: 'fl-stepper__step--current',
    rejected: 'fl-stepper__step--rejected',
    unreachable: 'fl-stepper__step--unreachable',
};

/**
 * Where a file stands. **Deux dispositions, et le consommateur choisit** : en
 * `horizontal`, un pas par colonne, repliés un par ligne sous 860 px : la réponse
 * de la maquette du portail à neuf libellés longs côte à côte ; en `vertical`, un
 * pas par ligne, la colonne de gauche du parcours d'onboarding.
 *
 * Il porte le nombre de pas qu'on lui donne : la frise ordinaire en a neuf, le
 * processus cadré trois, et rien ici n'est réglé pour l'un ou pour l'autre.
 *
 * Two choices come from the mockup and are worth keeping: a single marker is filled
 * (the current one) and the state is only written for it. Elsewhere the marker
 * already says it, so repeating « À venir » eight times only adds noise. The text
 * stays in the accessibility tree.
 */
const Stepper = ({ steps, caption, orientation = 'vertical', className }: Props) => (
    <ol
        className={clsx('fl-stepper', `fl-stepper--${orientation}`, className)}
        aria-label={caption}
        // The count drives the width the trail is allowed to spread over: three rungs
        // stretched across a wide sheet read as a trail missing its middle.
        style={{ '--fl-stepper-count': steps.length } as CSSProperties}
    >
        {steps.map(({ name, state, stateLabel }, index) => (
            <li
                key={name}
                className={clsx('fl-stepper__step', stepClassName[state])}
                aria-current={state === 'current' ? 'step' : undefined}
            >
                <span className="fl-stepper__marker" aria-hidden="true">
                    {/* LE NUMERO N'EST ECRIT QU'EN VERTICAL, et les deux fils ne
                        repondent pas a la meme question. Le vertical compte des etapes
                        qu'on FAIT, « 2 sur 4 », et son numero porte cette information ;
                        l'horizontal montre l'avancement d'un dossier qu'on SUBIT, ou le
                        lecteur ne fait rien, et un numero l'invite a se demander ce
                        qu'il doit faire de l'etape 5. La maquette V3 n'en pose aucun sur
                        sa frise (`.sourcing-etapes`), le marqueur disant tout. */}
                    {orientation === 'vertical' ? index + 1 : null}
                </span>
                <div className="fl-stepper__label">
                    <b className="fl-stepper__name">{name}</b>
                    {stateLabel && <small className="fl-stepper__state">{stateLabel}</small>}
                </div>
            </li>
        ))}
    </ol>
);

export default Stepper;
